import { useState, useEffect, useRef } from 'react';

// Intervalo de comprobación en milisegundos (60 segundos)
const CHECK_INTERVAL = 60 * 1000;

interface VersionData {
  version: string;
  builtAt: string;
}

const fetchVersion = async (): Promise<string | null> => {
  try {
    // Timestamp para evitar caché del navegador
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-store',
      headers: { 'Pragma': 'no-cache', 'Cache-Control': 'no-cache' }
    });
    if (!response.ok) return null;
    const data: VersionData = await response.json();
    return data.version || null;
  } catch {
    // Silent fail on network errors; reintentamos en el siguiente tick
    return null;
  }
};

export const useAutoUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // ponytail: ref en vez de state: el intervalo nunca se recrea ni lee
  // closures viejos, y un fallo de red en la primera lectura ya no bloquea
  // la detección para toda la vida de la pestaña.
  const currentVersionRef = useRef<string | null>(null);

  useEffect(() => {
    const applyVersion = (version: string | null) => {
      if (!version) return;
      if (!currentVersionRef.current) {
        currentVersionRef.current = version;
        console.log('📱 Versión actual de la app:', version);
      } else if (version !== currentVersionRef.current) {
        console.log('✨ Nueva actualización detectada:', version);
        setUpdateAvailable(true);
      }
    };

    const check = async () => applyVersion(await fetchVersion());
    check();

    // Canal principal: el servidor empuja la versión nueva por SSE, así el
    // aviso llega incluso con la pestaña en segundo plano (Chrome congela
    // los timers de los clientes ocultos).
    const es = new EventSource('/api/version/stream');
    es.onmessage = (event) => {
      try {
        applyVersion(JSON.parse(event.data).version);
      } catch {
        // mensaje malformed: el fallback polling lo cubre
      }
    };
    es.onerror = () => {
      // EventSource reintenta solo; si el servidor no tiene el endpoint
      // (dev), el fallback polling sigue haciendo su trabajo.
    };

    // Fallback: si SSE cayera (proxy, dev), seguir comprobando cada 60 s.
    const intervalId = setInterval(check, CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
      es.close();
    };
  }, []);

  const refreshApp = () => {
    // Forzar recarga desde el servidor y limpiar caché si es posible
    if (window.caches) {
      // Intentar limpiar cachés de Service Workers si existen
      caches.keys().then((names) => {
        for (const name of names) {
          caches.delete(name);
        }
      });
    }
    // Reload ignoring cache
    window.location.reload();
  };

  return { updateAvailable, refreshApp };
};
